import React, { useEffect, useRef, useState } from "react";
import { ButtonGroup } from "@mui/material";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import { defaultImageUrl } from "../../config_and_helpers/config";
import S3MediaImage from "../Generic/S3MediaImage";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";

/**
 * Represents a single item component within the shop.
 *
 * Quantity is pushed to the parent cart only on explicit user edits (not via an effect
 * on every parent re-render), avoiding a setCart feedback loop that overwrote cart clears.
 *
 * @param {object} props - The component props.
 * @param {number} props.index - The index of the item in the list.
 * @param {boolean} props.dividerStop - Flag to determine if a divider should be shown.
 * @param {object} props.item - The item data.
 * @param {string|number} props.supplierKey - The key of the supplier for this item.
 * @param {function} props.onItemChange - Callback when item quantity changes from user input.
 * @param {function} props.handleItemDelete - Callback function to handle item deletion.
 */
const ShopItemComponent = ({
  index,
  dividerStop,
  item,
  supplierKey,
  onItemChange,
  handleItemDelete,
}) => {
  const onItemChangeRef = useRef(onItemChange);
  onItemChangeRef.current = onItemChange;

  const [quantity, setQuantity] = useState(item.quantity);

  useEffect(() => {
    setQuantity(item.quantity);
  }, [item.quantity]);

  const commitQuantityToParent = nextQuantity => {
    onItemChangeRef.current(supplierKey, index, "quantity", nextQuantity);
  };

  const handleMinusClick = () => {
    const next = quantity <= 1 ? 1 : Number(quantity) - 1;
    setQuantity(next);
    commitQuantityToParent(next);
  };

  const handlePlusClick = () => {
    const next = Number(quantity) + 1;
    setQuantity(next);
    commitQuantityToParent(next);
  };

  const handleInputChange = value => {
    if (value === "" || value < 1) {
      setQuantity("");
      return;
    }
    const next = Number(value);
    setQuantity(next);
    commitQuantityToParent(next);
  };

  return (
    <Container key={item.cat_num} sx={{ padding: 2 }}>
      <Grid item xs={12} sm={6}>
        <Grid container alignItems="center">
          <Grid item xs={5}>
            <S3MediaImage
              url={item?.image_url}
              fallbackUrl={defaultImageUrl}
              alt={`product-${item.cat_num}`}
              width={200}
            />
          </Grid>
          <Grid item xs={4}>
            <Button
              variant="outlined"
              color="error"
              onClick={e => {
                handleItemDelete(e, supplierKey, index);
              }}
            >
              Remove
            </Button>
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={6}>
        <Typography variant="subtitle1">{item.name}</Typography>
      </Grid>

      <Grid item xs={6}>
        <Typography variant="subtitle1">{item.cat_num}</Typography>
      </Grid>

      <ButtonGroup
        disableElevation
        variant="contained"
        aria-label="Disabled elevation buttons"
      >
        <Button onClick={handleMinusClick}>-</Button>
        <TextField
          value={quantity}
          onChange={e => handleInputChange(e.target.value)}
          id="outlined-number"
          label="Quantity"
          InputLabelProps={{
            shrink: true,
          }}
          onKeyPress={e => {
            if (e.key.match(/[^0-9]/)) {
              e.preventDefault();
            }
          }}
        />
        <Button onClick={handlePlusClick}>+</Button>
      </ButtonGroup>

      {!dividerStop && <Divider sx={{ my: 4, borderColor: "#424242" }} />}
    </Container>
  );
};
export default ShopItemComponent;
